import { useCallback, useEffect, useState } from "react";
import type { AgencyFixKitViewV1 } from "@enrein/shared-contracts/contracts/fix-kit";
import type { ManualPaymentStatus } from "@enrein/shared-contracts/contracts/fix-kit";
import type { StudioApiClient } from "./api.js";
import { captureStudioConversionEvent } from "./conversion-tracking.js";
import { readStudioRuntimeConfig } from "./env.js";

export interface FixKitPanelProps {
  readonly api: StudioApiClient;
  readonly claimId: string | null;
}

export function FixKitPanel({ api, claimId }: FixKitPanelProps) {
  const [fixKit, setFixKit] = useState<AgencyFixKitViewV1 | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<ManualPaymentStatus>("PAID");
  const [pilotReference, setPilotReference] = useState("");
  const [pilotNote, setPilotNote] = useState("");

  const load = useCallback(async () => {
    if (!claimId) {
      setFixKit(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const kit = await api.getFixKit(claimId);
      setFixKit(kit);
    } catch (caught) {
      setFixKit(null);
      if (caught instanceof Error && !caught.message.includes("fix_kit_not_found")) {
        setError(caught.message);
      }
    } finally {
      setLoading(false);
    }
  }, [api, claimId]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    if (!claimId) return;
    setError(null);
    setMessage(null);
    try {
      const kit = await api.generateFixKit(claimId);
      setFixKit(kit);
      setMessage(kit.idempotentReplay ? "Fix Kit already generated." : "Fix Kit generated.");
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  const markPayment = async () => {
    if (!fixKit) return;
    setError(null);
    setMessage(null);
    try {
      const updated = await api.updateFixKitManualPayment(fixKit.fixKitId, {
        schemaVersion: "manual-payment-update.v1.0",
        status: paymentStatus,
        pilotReference: pilotReference.trim() || undefined,
        pilotNote: pilotNote.trim() || undefined,
      });
      setFixKit(updated);
      setMessage(`Payment status updated to ${updated.payment.status}.`);
      if (updated.payment.status === "PAID") {
        void captureStudioConversionEvent(readStudioRuntimeConfig(), {
          eventType: "PAID",
          metadata: { fixKitId: updated.fixKitId },
        });
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : String(caught));
    }
  };

  if (!claimId) {
    return <p className="muted">Select an opportunity to manage its Fix Kit.</p>;
  }

  return (
    <div className="fix-kit-panel">
      <div className="button-row">
        <button type="button" className="btn btn-primary" onClick={() => void generate()}>
          Generate Fix Kit
        </button>
        <button type="button" onClick={() => void load()} disabled={loading}>
          Refresh
        </button>
      </div>
      {message ? <p className="success">{message}</p> : null}
      {error ? <p className="error">{error}</p> : null}
      {fixKit ? (
        <>
          <dl className="detail-grid">
            <dt>Fix Kit ID</dt>
            <dd>{fixKit.fixKitId}</dd>
            <dt>Payment</dt>
            <dd>{fixKit.payment.status}</dd>
            <dt>Score</dt>
            <dd>
              {fixKit.sourceScore} — {fixKit.sourceScoreLabel}
            </dd>
          </dl>
          <section>
            <h4>Recommended fixes</h4>
            <ol>
              {fixKit.items.map((item) => (
                <li key={item.issueRef}>
                  <strong>{item.issueTitle}</strong> ({item.priority}) — {item.recommendedFix}
                </li>
              ))}
            </ol>
          </section>
          <p className="muted">{fixKit.nextStepProposal}</p>
          <div className="form-grid">
            <label>
              Payment status
              <select value={paymentStatus} onChange={(e) => setPaymentStatus(e.target.value as ManualPaymentStatus)}>
                <option value="UNPAID">UNPAID</option>
                <option value="PAID">PAID</option>
                <option value="REFUNDED">REFUNDED</option>
                <option value="VOID">VOID</option>
              </select>
            </label>
            <label>
              Pilot reference
              <input value={pilotReference} onChange={(e) => setPilotReference(e.target.value)} />
            </label>
            <label>
              Pilot note
              <input value={pilotNote} onChange={(e) => setPilotNote(e.target.value)} />
            </label>
          </div>
          <button type="button" className="btn btn-secondary" onClick={() => void markPayment()}>
            Update manual payment
          </button>
        </>
      ) : loading ? (
        <p>Loading Fix Kit…</p>
      ) : (
        <p className="muted">No Fix Kit yet — generate from this opportunity.</p>
      )}
    </div>
  );
}
